/**
 * TeamSection (TEAM-01) — the technician admin. Lists the active crew; tap a
 * tech to open the full profile drawer (photo, bio, employment from the mirror,
 * skills, and Pegasus-native pay + commission gated behind team.pay). The ESC
 * import seeded existing techs; the drawer is where NEW HIRES get a face + pay.
 */
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, SlidersHorizontal, Plus, X, Check, RotateCcw, Loader2, Thermometer } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useOrgSettingJson, useSaveOrgSetting } from "@/lib/orgSettings";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { TechAvatar } from "@/components/TechAvatar";
import { InfoTip } from "@/components/InfoTip";
import { TechProfileDrawer } from "./TechProfileDrawer";
import { deptRoleName } from "./deptRole";
import { cn } from "@/lib/cn";

interface TechRow {
  empno: string;
  empname: string | null;
  firstname: string | null;
  lastname: string | null;
  role: string | null; // Pegasus override (tech_profile.role_id → name)
  dept: string | null; // ESC default department (for the derived role)
  title: string | null;
}

// Effective role = explicit Pegasus override, else derived from the ESC department.
const effectiveRole = (t: TechRow): string | null => t.role ?? deptRoleName(t.dept);

// ESC holding-bucket pseudo-employees are not real people (reference_pseudo_employees).
const PSEUDO = new Set(["0000", "0001", "1000", "1001", "1002", "1003", "7777", "8888", "9999"]);

// Supabase errors are plain objects, not Error instances — surface their .message.
const errText = (e: unknown): string =>
  e instanceof Error ? e.message
  : e && typeof e === "object" && "message" in e ? String((e as { message: unknown }).message)
  : String(e);

function useActiveTechs() {
  return useQuery({
    queryKey: ["team_techs"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<TechRow[]> => {
      const [empRes, profRes, roleRes] = await Promise.all([
        supabase.from("mirror_employee").select("empno, empname, firstname, lastname, dept").eq("inactive", false).order("empname"),
        supabase.from("tech_profile").select("empno, role_id, title"),
        supabase.from("team_role").select("id, name"),
      ]);
      if (empRes.error) throw empRes.error;
      const roleName = new Map<number, string>();
      for (const r of (roleRes.data ?? []) as { id: number; name: string }[]) roleName.set(r.id, r.name);
      const prof = new Map<string, { role_id: number | null; title: string | null }>();
      for (const p of (profRes.data ?? []) as { empno: string; role_id: number | null; title: string | null }[]) prof.set(p.empno, p);
      return ((empRes.data ?? []) as Omit<TechRow, "role" | "title">[])
        .filter((t) => !PSEUDO.has(t.empno))
        .map((t) => {
          const p = prof.get(t.empno);
          return { ...t, role: p?.role_id != null ? roleName.get(p.role_id) ?? null : null, title: p?.title ?? null };
        });
    },
  });
}

function nameOf(t: TechRow): string {
  const n = [t.firstname, t.lastname].filter(Boolean).join(" ");
  return n || t.empname || t.empno;
}

interface RoleAdmin { id: number; name: string; sort: number | null; archived: boolean }

function useAllRoles() {
  return useQuery({
    queryKey: ["team_roles_admin"],
    queryFn: async (): Promise<RoleAdmin[]> => {
      const { data, error } = await supabase.from("team_role").select("id, name, sort, archived").order("sort");
      if (error) throw error;
      return (data ?? []) as RoleAdmin[];
    },
  });
}

export function TeamSection() {
  const { data: techs, isLoading } = useActiveTechs();
  const [sel, setSel] = useState<TechRow | null>(null);
  const [manageRoles, setManageRoles] = useState(false);

  return (
    <section>
      <div className="flex items-start justify-end gap-3">
        <button
          type="button"
          onClick={() => setManageRoles((v) => !v)}
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors",
            manageRoles ? "border-brand-200 bg-brand-50 text-brand-700" : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
          )}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" /> Job Titles & Departments
        </button>
      </div>

      {manageRoles && <RoleManager />}

      <div className="mt-4 divide-y divide-slate-100 overflow-hidden rounded-xl border border-hairline bg-white">
        {isLoading ? (
          <div className="p-6 text-center text-sm text-slate-400">Loading the team…</div>
        ) : (techs ?? []).length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">No active technicians found.</div>
        ) : (
          (techs ?? []).map((t) => {
            const name = nameOf(t);
            return (
              <button
                key={t.empno}
                type="button"
                onClick={() => setSel(t)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50"
              >
                <TechAvatar empno={t.empno} name={name} size="lg" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900">{name}</div>
                  <div className="text-xs text-slate-500">
                    {effectiveRole(t) || t.title || <span className="text-slate-400">No role set</span>}
                    <span className="text-slate-300"> · #{t.empno}</span>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
              </button>
            );
          })
        )}
      </div>

      <SickPolicyCard />

      <TechProfileDrawer
        empno={sel?.empno ?? null}
        name={sel ? nameOf(sel) : ""}
        onClose={() => setSel(null)}
      />
    </section>
  );
}

/**
 * SICK-02 (Seth 2026-09-02) — the company's sick-day notice rule. The phone
 * shows the rule on the "Out today?" card; the DB flags a same-day report
 * filed after the cutoff as LATE NOTICE (office DM + board chip). Category
 * (sick / family / safety / other) is always asked; the medical reason never
 * is — MN ESST lets the employer require documentation only after more than
 * two consecutive scheduled workdays, which the office DM points out on day 3.
 */
function SickPolicyCard() {
  const canManage = useCan("settings.people");
  const cutoff = useOrgSettingJson<string>("sick_notice_cutoff", "06:30").data ?? "06:30";
  const rule = useOrgSettingJson<string>("sick_notice_rule", "Report before 6:30 AM and call the office.").data ?? "";
  const save = useSaveOrgSetting();
  const [cut, setCut] = useState<string | null>(null);
  const [txt, setTxt] = useState<string | null>(null);
  const cutVal = cut ?? cutoff;
  const txtVal = txt ?? rule;
  const dirty = cutVal !== cutoff || txtVal !== rule;
  return (
    <div className="mt-4 rounded-xl border border-hairline bg-white p-5 shadow-e1">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <Thermometer className="h-4 w-4 text-brand-600" /> Sick-Day Notice
        <InfoTip label="How sick-day notice works">
          Techs report an out day from the phone. A same-day report filed after the cutoff is marked late notice on the office
          message and the board.
        </InfoTip>
      </h3>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="text-xs text-slate-500">
          <span className="mb-1 block font-medium">Notice cutoff</span>
          <input type="time" value={cutVal} disabled={!canManage} onChange={(e) => setCut(e.target.value)}
            className="rounded-md border border-hairline bg-white px-2 py-1.5 text-sm text-slate-800 focus:border-brand-400 focus:outline-none" />
        </label>
        <div className="min-w-[280px] flex-1 text-xs text-slate-500">
          <div className="mb-1 flex items-center gap-1.5">
            <label htmlFor="sick-notice-rule" className="font-medium">Rule shown to techs</label>
            <InfoTip label="About the notice rule">
              Documentation may be requested after more than two consecutive scheduled workdays (Minnesota ESST); the office
              message says so on the third day. Confirm policy wording with your HR or payroll advisor.
            </InfoTip>
          </div>
          <input id="sick-notice-rule" value={txtVal} disabled={!canManage} onChange={(e) => setTxt(e.target.value)}
            className="w-full rounded-md border border-hairline bg-white px-2 py-1.5 text-sm text-slate-800 focus:border-brand-400 focus:outline-none" />
        </div>
        {canManage && (
          <button type="button" disabled={!dirty || save.isPending}
            onClick={async () => {
              if (cutVal !== cutoff) await save.mutateAsync({ key: "sick_notice_cutoff", value: cutVal });
              if (txtVal !== rule) await save.mutateAsync({ key: "sick_notice_rule", value: txtVal });
              setCut(null); setTxt(null);
            }}
            className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-800 disabled:opacity-40">
            <Check className="h-3.5 w-3.5" /> Save
          </button>
        )}
      </div>
    </div>
  );
}

// ---- Role manager (Pegasus-managed role list; add / rename / archive) ----
function RoleManager() {
  const qc = useQueryClient();
  const { data: roles } = useAllRoles();
  const [busy, setBusy] = useState<number | "new" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const all = roles ?? [];
  const active = all.filter((r) => !r.archived);
  const archived = all.filter((r) => r.archived);

  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["team_roles_admin"] }),
      qc.invalidateQueries({ queryKey: ["team_roles"] }),
      qc.invalidateQueries({ queryKey: ["team_techs"] }),
    ]);

  const run = async (id: number | "new", fn: () => Promise<{ error: unknown }>) => {
    setErr(null); setBusy(id);
    try {
      const { error } = await fn();
      if (error) throw error;
      await refresh();
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(null); }
  };

  const rename = (id: number, name: string) =>
    run(id, async () => supabase.from("team_role").update({ name }).eq("id", id));
  const setArchived = (id: number, archivedVal: boolean) =>
    run(id, async () => supabase.from("team_role").update({ archived: archivedVal }).eq("id", id));
  const addRole = () => {
    const name = newName.trim();
    if (!name) return;
    const maxSort = Math.max(0, ...all.map((r) => r.sort ?? 0));
    run("new", async () => {
      const res = await supabase.from("team_role").insert({ name, sort: maxSort + 10, archived: false });
      if (!res.error) setNewName("");
      return res;
    });
  };

  return (
    <div className="mt-3 rounded-xl border border-brand-100 bg-brand-50/40 p-4">
      <div className="flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-[13px] font-bold text-slate-800">
          Job Titles &amp; Departments
          <InfoTip label="About job titles and departments">These fill the Department / role dropdown on every tech.</InfoTip>
        </h4>
        {archived.length > 0 && (
          <button type="button" onClick={() => setShowArchived((v) => !v)} className="text-[11px] font-medium text-slate-400 hover:text-slate-600">
            {showArchived ? "Hide archived" : `Show archived (${archived.length})`}
          </button>
        )}
      </div>

      <div className="mt-3 space-y-1.5">
        {active.map((r) => (
          <RoleRow key={r.id} role={r} busy={busy === r.id} onRename={rename} onArchive={(id) => setArchived(id, true)} />
        ))}
        {active.length === 0 && <p className="text-sm text-slate-400">No roles yet — add the first one below.</p>}
      </div>

      {showArchived && archived.length > 0 && (
        <div className="mt-3 space-y-1.5 border-t border-brand-100 pt-3">
          {archived.map((r) => (
            <div key={r.id} className="flex items-center gap-2">
              <span className="flex-1 truncate text-sm text-slate-400 line-through">{r.name}</span>
              <button type="button" onClick={() => setArchived(r.id, false)} disabled={busy === r.id}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-brand-600 hover:bg-brand-50">
                {busy === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Restore
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2 border-t border-brand-100 pt-3">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addRole(); }}
          placeholder="New role (e.g. Comfort Advisor)"
          className="h-9 flex-1 rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
        />
        <button type="button" onClick={addRole} disabled={busy === "new" || !newName.trim()}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50">
          {busy === "new" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
        </button>
      </div>
      {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
    </div>
  );
}

function RoleRow({ role, busy, onRename, onArchive }: {
  role: RoleAdmin; busy: boolean; onRename: (id: number, name: string) => void; onArchive: (id: number) => void;
}) {
  const [name, setName] = useState(role.name);
  useEffect(() => setName(role.name), [role.name]);
  const dirty = name.trim().length > 0 && name.trim() !== role.name;

  return (
    <div className="flex items-center gap-2">
      <input
        value={name}
        disabled={busy}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && dirty) onRename(role.id, name.trim()); }}
        className="h-9 flex-1 rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:bg-slate-50"
      />
      {dirty ? (
        <button type="button" onClick={() => onRename(role.id, name.trim())} disabled={busy}
          className="inline-flex items-center gap-1 rounded-md bg-brand-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50" title="Save name">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        </button>
      ) : (
        <button type="button" onClick={() => onArchive(role.id)} disabled={busy}
          className="flex h-8 w-8 items-center justify-center rounded-md text-slate-300 transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-50" title={`Archive ${role.name}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
}
